import * as React from "react";

import { faCloud, faHdd } from "@fortawesome/free-solid-svg-icons";
import { Box, Divider } from "@mui/material";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { Form } from "src/common/components/Form";
import Loader from "src/common/components/Loader";
import { FontAwesomeIconWithTheme } from "src/common/components/ui/IconWrapper";
import { CreateTemplateGroupDTO } from "src/domain/administrative/resource-management/features/templates/api/createTemplateGroup";
import { useUpdateTemplateGroup } from "src/domain/administrative/resource-management/features/templates/api/updateTemplateGroups";
import { GroupInput } from "src/domain/administrative/resource-management/features/templates/components/GroupInput";
import { compareTemplateGroups } from "src/domain/administrative/resource-management/features/templates/utils/groupSort";
import { useUpdateDisk } from "src/domain/core/disks";
import { TemplateNameInput, UpdateTemplateDTO } from "src/domain/core/templates";
import { BasicInfoInput } from "src/domain/core/workflow/components/create/BasicInfoInput";
import { DiskInfoInput, TemplateDiskInfo } from "src/domain/core/workflow/components/create/DiskInput";
import { ExpandableSettings } from "src/domain/core/workflow/components/create/ExpandableSettings";
import { NodeInput } from "src/domain/core/workflow/components/create/NodeInput";
import { SchedulerInput } from "src/domain/core/workflow/components/create/SchedulerInput";
import { TagsInput } from "src/domain/core/workflow/components/create/TagsInput";
import { ALLOWED_MAX_DISK_SIZE } from "src/lib/const";

const ButtonGroup = styled("div")(() => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
}));

const SubHeading = styled("div")(() => ({
  marginBottom: "25px",

  svg: {
    marginRight: "5px",
  },
}));

export type UpdateFormInput = {
  newDiskName: string;
  newDiskSize: number;
  existingDiskName: string;
  existingDiskSize: number;
  name: string;
  is_gpu: boolean;
  head_node_instance_type: string;
  compute_node_instance_type: string;
  stack_id: number;
  volume_id?: string;
  tags: string;
  scheduler_id: number;
  templateName: string;
  blueprint: number;
  groups: string[];
};

const validationSchema = (
  availableDisks: Disk[],
  existingDiskSize: number,
  templates: Template[],
  templateName: string
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const validation: Record<string, any> = {
    name: Yup.string()
      .required("Workflow name is required")
      .matches(/^[a-zA-Z](.*)+$/, "Must start with a letter")
      .matches(/^[a-zA-Z0-9]+$/, "Can only contain letters or numbers")
      .min(4, "Must be at least 4 characters")
      .max(40, "Must be 40 characters or less"),
    scheduler_id: Yup.number().required("Scheduler is required"),
    head_node_instance_type: Yup.string().required("Head node is required"),
    compute_node_instance_type: Yup.string().required("Compute node is required"),
    tags: Yup.string().test("is-json", "Provided tags are not valid JSON", (value) => {
      try {
        // Try to parse JSON to ensure proper format, if it fails will return false in catch clause
        if (value) {
          JSON.parse(value);
        }
        return true;
      } catch (error) {
        return false;
      }
    }),
    // Disk Input
    newDiskName: Yup.string().when(
      ["$newDiskDisabled", "$existingDiskDisabled"],
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      (newDiskDisabled: boolean, existingDiskDisabled: boolean, schema) => {
        if (newDiskDisabled || (!newDiskDisabled && !existingDiskDisabled)) {
          return schema.notRequired();
        }

        return schema
          .required("Disk name is required")
          .test("diskCheck", "Disk already exists with this name", (item: string) => {
            const lowercaseDisks = availableDisks?.map((disk) => disk.name.toLowerCase());
            return !lowercaseDisks.includes(item.toLowerCase());
          });
      }
    ),
    newDiskSize: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when(
        ["$newDiskDisabled", "$existingDiskDisabled"],
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        (newDiskDisabled: boolean, existingDiskDisabled: boolean, schema) => {
          if (newDiskDisabled || (!newDiskDisabled && !existingDiskDisabled)) {
            return schema.notRequired();
          }

          return schema
            .transform((value) => (isNaN(value) ? undefined : value))
            .required("Enter a valid disk size")
            .min(5, "Must be greater than or equal to 5")
            .max(ALLOWED_MAX_DISK_SIZE, `Must be less than or equal to ${ALLOWED_MAX_DISK_SIZE}`);
        }
      ),
    existingDiskName: Yup.string().when(
      ["$existingDiskDisabled", "$newDiskDisabled"],
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      (existingDiskDisabled: boolean, newDiskDisabled: boolean, schema) => {
        if (existingDiskDisabled || (!existingDiskDisabled && !newDiskDisabled)) {
          return schema.notRequired();
        }
        return schema.required();
      }
    ),
    existingDiskSize: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when(
        ["$existingDiskDisabled", "$newDiskDisabled"],
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        (existingDiskDisabled: boolean, newDiskDisabled: boolean, schema) => {
          if (existingDiskDisabled || (!existingDiskDisabled && !newDiskDisabled)) {
            return schema.notRequired();
          }
          return schema
            .transform((value) => (isNaN(value) ? undefined : value))
            .required("Enter a valid disk size")
            .min(existingDiskSize, `Must be greater than or equal to ${existingDiskSize}`)
            .max(ALLOWED_MAX_DISK_SIZE, `Must be less than or equal to ${ALLOWED_MAX_DISK_SIZE}`);
        }
      ),
    templateName: Yup.string()
      .required("Template name is required")
      .matches(/^[a-zA-Z0-9 ]+$/, "Only numbers, letters, and spaces are allowed")
      .test("unique", "Template Name must be unique", (value) => {
        return !templates.find((template) => {
          return template?.templateName === value && template?.templateName !== templateName;
        });
      }),
    blueprint: Yup.string().required("Blueprint is required"),
  };

  return validation;
};

interface UpdateTemplateProps {
  nodeData: {
    head_node_instance_types: MachineInstanceTypeDesc[];
    compute_node_instance_types: MachineInstanceTypeDesc[];
    gpu_head_node_instance_types: MachineInstanceTypeDesc[];
    gpu_compute_node_instance_types: MachineInstanceTypeDesc[];
  };
  blueprints: WorkflowBlueprintDesc[];
  template: Template;
  templates: Template[];
  close: () => void;
  disks: Disk[];
  disksError: Error;
  templateGroups?: WorkflowTemplateGroup[];
  groups?: Group[];
  updateTemplate: (template: UpdateTemplateDTO) => any;
}

export const UpdateTemplateForm: React.FC<UpdateTemplateProps> = ({
  nodeData,
  blueprints,
  template,
  templates,
  close,
  disks,
  disksError,
  templateGroups,
  groups,
  updateTemplate,
}) => {
  const availableDisks = disks?.filter((disk) => disk?.status === "available");
  const inUseDisks = disks?.filter((disk) => disk?.status === "in-use");

  const [submitting, setSubmitting] = React.useState(false);

  const updateDisk = useUpdateDisk();
  const updateTemplateGroup = useUpdateTemplateGroup();
  const [newDiskDisabled, setNewDiskDisabled] = React.useState<boolean>(false);
  const [existingDiskDisabled, setExistingDiskDisabled] = React.useState<boolean>(false);
  const [existingDiskSize, setExistingDiskSize] = React.useState<number>(null);
  const [blueprintId, setBlueprintId] = React.useState<number>(template.blueprintId);
  const [userSchedulersError, setUserSchedulersError] = React.useState<Error>(null);
  const [blueprintSchedulersError, setBlueprintSchedulersError] = React.useState<Error>(null);
  const sortedTemplateGroups = React.useMemo(() => {
    const sorted = templateGroups?.sort((a, b) => compareTemplateGroups(a, b));
    return sorted?.map((tg) => tg.group_name);
  }, [templateGroups]);

  if (userSchedulersError || blueprintSchedulersError) {
    return (
      <div className="centered-disp">
        Unable to load edit template form. Please try refreshing page. If problem persists, please reach out to support.
      </div>
    );
  }

  const updateTemplateGroups = async (
    templateGroups: WorkflowTemplateGroup[],
    groups: string[],
    templateId: number
  ) => {
    const groupNames = templateGroups?.map((tg) => tg.group_name);
    const deleteRows = templateGroups?.filter((tg) => !groups?.includes(tg.group_name));
    const createRows: CreateTemplateGroupDTO[] = [];
    for (const group of groups) {
      if (!groupNames?.includes(group)) {
        createRows.push({ workflow_template_id: templateId, group_name: group } as CreateTemplateGroupDTO);
      }
    }
    return await updateTemplateGroup(createRows, deleteRows);
  };

  const submit = async (values: UpdateFormInput) => {
    setSubmitting(true);

    const resp = await submitRequest(values);
    if (resp?.ok) {
      setSubmitting(false);
      close();
      return;
    }

    setSubmitting(false);
  };

  const findInstanceTypeByName = (instanceList: MachineInstanceTypeDesc[], name: string): string => {
    return instanceList.find((instance) => instance.name === name)?.instance_type;
  };

  const submitRequest = async (values: UpdateFormInput) => {
    // If selecting existing disk, check if the size is being updated. If so, trigger an update disk API call
    if (values.existingDiskName) {
      const disk = availableDisks?.find((disk) => disk.name === values.existingDiskName);
      if (disk && values.existingDiskSize > disk.size) {
        const resp = await updateDisk(disk.id, values.existingDiskSize);
        // If request to update disk failed, don't continue any further with creating a new workflow
        if (resp?.ok === false) {
          toast.error("Disk size failed to update, exiting workflow template creation");
          return resp;
        }
      }
    }

    const headNode = values.is_gpu
      ? findInstanceTypeByName(nodeData?.gpu_head_node_instance_types || [], values.head_node_instance_type)
      : findInstanceTypeByName(nodeData?.head_node_instance_types || [], values.head_node_instance_type);

    const computeNode = values.is_gpu
      ? findInstanceTypeByName(nodeData?.gpu_compute_node_instance_types || [], values.compute_node_instance_type)
      : findInstanceTypeByName(nodeData?.compute_node_instance_types || [], values.compute_node_instance_type);

    const blueprint = blueprints.find((blueprint) => blueprint.name === values.blueprint);

    let req: UpdateTemplateDTO = {
      workflow_name: values.name,
      template_name: values.templateName,
      blueprint_id: blueprint.id,
      head_node_instance_type: headNode,
      compute_node_instance_type: computeNode,
      tags: values.tags,
      scheduler_id: values.scheduler_id,
      is_gpu: values.is_gpu,
      id: template.id,
    };

    const volumeId = [...(availableDisks || []), ...(inUseDisks || [])].find(
      (disk) => disk.name === values.existingDiskName
    )?.id;

    if (volumeId) {
      req = { ...req, volume_id: volumeId };
    }

    if (values.groups) {
      await updateTemplateGroups(templateGroups, values.groups, template.id);
    }

    return await updateTemplate(req);
  };

  const validation = validationSchema(availableDisks, existingDiskSize, templates, template.templateName);
  const schema = Yup.object().shape(validation, [
    ["newDiskName", "existingDiskName"],
    ["newDiskSize", "existingDiskSize"],
  ]);

  const findDiskIndex = (volumeId: string): number => {
    return disks?.findIndex((disk) => disk.id === volumeId);
  };
  const workflowName = template?.body.workflowName || "";
  const tagsDefault = template?.body.tags || "";
  const diskIndex = findDiskIndex(template?.body.volumeId);
  const diskInfo: TemplateDiskInfo = {
    id: template?.body.volumeId || "",
    name: diskIndex >= 0 ? disks[diskIndex].name : "",
    size: diskIndex >= 0 ? disks[diskIndex].size : null,
    isEditForm: true,
  };
  const existingDiskNameDefault = diskInfo.name;
  const existingDiskSizeDefault = diskInfo.size;
  const blueprintDefault = blueprints?.find((blueprint) => blueprint.id === blueprintId)?.name;

  return (
    <Form<UpdateFormInput, typeof schema>
      noValidate // Disable native HTML validation and use Yup for entire form
      id="edit-template"
      onSubmit={submit}
      autoComplete={"off"}
      options={{
        context: {
          newDiskDisabled: newDiskDisabled,
          existingDiskDisabled: existingDiskDisabled,
        },
        defaultValues: {
          name: workflowName,
          blueprint: blueprintDefault,
          groups: sortedTemplateGroups,
          tags: tagsDefault,
          templateName: template.templateName,
          existingDiskName: existingDiskNameDefault,
          existingDiskSize: existingDiskSizeDefault,
        },
      }}
      schema={schema}
    >
      {({ register, formState, setValue, getValues, resetField, clearErrors, control, watch }) => {
        return (
          <>
            <Box>
              <SubHeading>
                <Typography color="textPrimary" variant="h5">
                  <FontAwesomeIconWithTheme icon={faCloud} />
                  Workflow Information
                </Typography>
                <Typography color="textSecondary" variant="body1">
                  Enter information for your workflow
                </Typography>
              </SubHeading>
              <BasicInfoInput
                register={register}
                clearErrors={clearErrors}
                formState={formState}
                setValue={setValue}
                watch={watch}
                template={template}
                blueprints={blueprints}
                blueprintName={template.blueprintName}
                setBlueprintId={setBlueprintId}
                source="update-template"
              />
              <NodeInput
                resetField={resetField}
                control={control}
                params={{
                  head_node_instance_types: nodeData?.head_node_instance_types,
                  compute_node_instance_types: nodeData?.compute_node_instance_types,
                  gpu_head_node_instance_types: nodeData?.gpu_head_node_instance_types,
                  gpu_compute_node_instance_types: nodeData?.gpu_compute_node_instance_types,
                  template: template,
                }}
                gpuInitial={template?.body.isGPU}
                register={register}
              />
              <SchedulerInput
                setUserSchedulersError={setUserSchedulersError}
                setBlueprintSchedulersError={setBlueprintSchedulersError}
                template={template}
                blueprintId={blueprintId}
                formState={formState}
                register={register}
                setInitializeExpanded={null}
              />

              <ExpandableSettings
                label="Show tag settings"
                initializeAsExpanded={false}
                errors={formState.errors?.tags}
              >
                <TagsInput register={register} formState={formState} />
              </ExpandableSettings>

              <Divider
                sx={{
                  margin: "20px 10px",
                }}
              />
              {template.userId && (
                <>
                  <SubHeading>
                    <Typography color="textPrimary" variant="h5">
                      <FontAwesomeIconWithTheme icon={faHdd} />
                      Disk Information
                    </Typography>

                    <Typography color="textSecondary" variant="body1">
                      {availableDisks
                        ? "Complete one of the following disk selections"
                        : "Create a new disk to use with this workflow"}
                    </Typography>
                  </SubHeading>
                  <DiskInfoInput
                    register={register}
                    formState={formState}
                    clearErrors={clearErrors}
                    availableDisks={availableDisks}
                    availableDisksFetchError={disksError}
                    inUseDisks={inUseDisks}
                    diskInfo={diskInfo}
                    setValue={setValue}
                    getValues={getValues}
                    control={control}
                    disabled={{
                      newDiskDisabled,
                      existingDiskDisabled,
                      setNewDiskDisabled,
                      setExistingDiskDisabled,
                    }}
                    setExistingDiskSize={setExistingDiskSize}
                  />

                  <Divider
                    sx={{
                      margin: "20px 10px 30px 10px",
                    }}
                  />

                  <TemplateNameInput register={register} formState={formState} />
                </>
              )}
              {!template.userId && (
                <>
                  <GroupInput
                    formState={formState}
                    templateGroups={sortedTemplateGroups || []}
                    groups={groups}
                    setValue={setValue}
                  ></GroupInput>
                  <TemplateNameInput register={register} formState={formState} />
                </>
              )}
            </Box>

            <ButtonGroup>
              <Button size="small" disabled={submitting} variant="contained" onClick={close} sx={{ margin: "5px" }}>
                Cancel
              </Button>

              <Button type="submit" disabled={submitting} size="small" variant="contained" sx={{ margin: "5px" }}>
                Save Template
              </Button>
              <div className="small-loader" role="loader">
                {submitting && <Loader />}
              </div>
            </ButtonGroup>
          </>
        );
      }}
    </Form>
  );
};
